import React from "react";
import ReactDOM from "react-dom";
import { QRCodeCanvas } from "qrcode.react";

/** Must match `.processed-order-print-qr-img` (width / height). */
export const PDF_QR_DISPLAY_SIZE_PX = 100;

/**
 * Renders a QR into an off-screen canvas via qrcode.react, then returns a PNG data URL.
 * This path does not depend on backend image payload length or html2canvas data-URL quirks.
 */
function generateQrPngDataUrlFromValue(text) {
  return new Promise((resolve, reject) => {
    const container = document.createElement("div");
    container.setAttribute("aria-hidden", "true");
    container.style.cssText =
      "position:fixed;left:-9999px;top:0;width:0;height:0;overflow:hidden;";

    document.body.appendChild(container);

    const cleanup = () => {
      try {
        ReactDOM.unmountComponentAtNode(container);
      } catch (_e) {
        /* ignore */
      }
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };

    try {
      ReactDOM.render(
        <QRCodeCanvas
          value={text}
          size={PDF_QR_DISPLAY_SIZE_PX}
          includeMargin={false}
          level="M"
        />,
        container,
        () => {
          window.setTimeout(() => {
            try {
              const canvas = container.querySelector("canvas");
              if (!canvas) {
                cleanup();
                resolve(null);
                return;
              }
              resolve(canvas.toDataURL("image/png"));
            } catch (e) {
              cleanup();
              reject(e);
              return;
            }
            cleanup();
          }, 120);
        },
      );
    } catch (e) {
      cleanup();
      reject(e);
    }
  });
}

/**
 * Draws any image URL (including data:image) into a square PNG data URL for stable PDF capture.
 */
function rasterizeImageToSquarePng(src, size) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = size;
        c.height = size;
        const ctx = c.getContext("2d");
        if (!ctx) {
          resolve(src.startsWith("data:") ? src : null);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        resolve(c.toDataURL("image/png"));
      } catch (_e) {
        resolve(src.startsWith("data:") ? src : null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Produces a single PNG data URL for the print layout QR slot: prefer client-generated QR from
 * `qrCodeValue`, else normalize `qrCodeImageSrc`.
 *
 * @param {{ qrCodeValue?: string, qrCodeImageSrc?: string | null }} vmFields
 * @returns {Promise<string | null>}
 */
export async function preparePdfQrDataUrl(vmFields) {
  const { qrCodeValue, qrCodeImageSrc } = vmFields || {};
  const value =
    qrCodeValue != null && String(qrCodeValue).trim() !== ""
      ? String(qrCodeValue).trim()
      : "";
  const fallbackSrc =
    qrCodeImageSrc != null && String(qrCodeImageSrc).trim() !== ""
      ? String(qrCodeImageSrc).trim()
      : "";

  if (value) {
    try {
      const generated = await generateQrPngDataUrlFromValue(value);
      if (generated) return generated;
    } catch (_e) {
      /* fall through to fallback */
    }
  }

  if (fallbackSrc) {
    return rasterizeImageToSquarePng(fallbackSrc, PDF_QR_DISPLAY_SIZE_PX);
  }

  return null;
}
